import { useState, useEffect, Component } from 'react'
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom'

import { StoreProvider, useStore } from './store/StoreContext'
import { I18nProvider }            from './i18n/I18nContext'
import { LANG_META }               from './i18n/translations'
import { AuthProvider, useAuth }   from './auth/AuthContext'
import LoginPage                   from './auth/LoginPage'
import AuthCallback                from './auth/AuthCallback'
import LandingPage                 from './website/LandingPage'
import PublicQuickScreener from './website/PublicQuickScreener'
import Sidebar          from './shell/Sidebar'
import TopBar           from './shell/TopBar'
import SaveToast        from './shell/SaveToast'
import Dashboard        from './shell/Dashboard'
import ChildrenView     from './shell/ChildrenView'
import SettingsView     from './shell/SettingsView'
import TeacherDashboard from './shell/TeacherDashboard'
import ParentDashboard  from './shell/ParentDashboard'
import AdminPanel       from './shell/AdminPanel'
import ResearchDashboard from './shell/ResearchDashboard'
import ResearchChildDashboard from './shell/ResearchChildDashboard'
import ResearchVariablePage from './shell/ResearchVariablePage'
import ResearchCalibrationPage from './shell/ResearchCalibrationPage'
import ClinicalRatingForm from './shell/ClinicalRatingForm'
import ProfilePage      from './shell/ProfilePage'
import AccessibilityFloat from './shell/AccessibilityFloat'

import CollectionManager  from './modules/collection'
import FlashcardGenerator from './modules/flashcard'
import FlashcardLearning  from './shell/FlashcardLearning'
import WorksheetLearning  from './shell/WorksheetLearning'
import StoryBookLearning  from './shell/StoryBookLearning'

import ActivityCardLearning from './shell/ActivityCardLearning'
import WorksheetCreator     from './modules/worksheet'
import ActivityCards        from './modules/activity'
import StoryBooks           from './modules/storybook'
import WorkPlan             from './modules/workplan'
import ProgressTracker      from './modules/progress'
import SchoolReadiness      from './modules/readiness'
import QuickScreener        from './modules/readiness/QuickScreener'   // ← NEW
import BatchExport          from './modules/batch-export'
import PublicSRTReports     from './website/PublicSRTReports'

// ── Error Boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  state = { error: null }
  componentDidCatch(err) { this.setState({ error: err }) }
  render() {
    if (this.state.error) return (
      <div style={{ padding:40, color:'red', fontFamily:'monospace', whiteSpace:'pre-wrap' }}>
        <strong>CRASH:</strong> {this.state.error.message}
        <br/>{this.state.error.stack}
      </div>
    )
    return this.props.children
  }
}

// ── Unauthorized ──────────────────────────────────────────────────────────────
function UnauthorizedPage() {
  const { user, logout } = useAuth()
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--cream)' }}>
      <div style={{ textAlign:'center', maxWidth:360 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔒</div>
        <div style={{ fontSize:22, fontWeight:800, color:'var(--navy)', marginBottom:8 }}>Access Denied</div>
        <div style={{ fontSize:14, color:'var(--mist)', marginBottom:24, lineHeight:1.6 }}>
          Your account <strong>({user?.role})</strong> doesn't have permission to view this page.
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <a href="/" style={{ padding:'9px 20px', background:'var(--navy)', color:'white', borderRadius:8, fontWeight:700, fontSize:13, textDecoration:'none' }}>Go Home</a>
          <button onClick={logout} style={{ padding:'9px 20px', background:'white', color:'#666', border:'1.5px solid var(--border)', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>Log Out</button>
        </div>
      </div>
    </div>
  )
}

// ── RequireAuth ───────────────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--cream)' }}>
      <div style={{ width:24, height:24, border:'3px solid var(--sky)', borderTop:'3px solid transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>
    </div>
  )
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  return children
}

// ── RequireRole ───────────────────────────────────────────────────────────────
function RequireRole({ roles, children }) {
  const { user } = useAuth()
  if (!user)                      return <Navigate to="/login"        replace />
  if (!roles.includes(user.role)) return <Navigate to="/unauthorized" replace />
  return children
}

// ── RoleDashboard ─────────────────────────────────────────────────────────────
function RoleDashboard({ onNav }) {
  const { user } = useAuth()
  if (!user) return null
  if (user.role === 'teacher')    return <TeacherDashboard  onNav={onNav} />
  if (user.role === 'parent')     return <ParentDashboard   onNav={onNav} />
  if (user.role === 'researcher') return <ResearchDashboard onNav={onNav} />
  return <Dashboard onNav={onNav} />
}

// ── Shell layout ──────────────────────────────────────────────────────────────
function Shell() {
  const { saveStatus, settings } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [lang, setLang]               = useState('en')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile]       = useState(false)

  useEffect(() => {
    if (settings?.language && settings.language !== lang) setLang(settings.language)
  }, [settings])

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768)
    fn()
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  useEffect(() => {
    if (isMobile) setSidebarOpen(false)
  }, [location.pathname])

  const meta   = LANG_META[lang] || LANG_META.en
  const active = location.pathname.replace('/', '') || 'dashboard'

  // ── handleNav: accepts optional state object for route context passing ──
  // Usage:
  //   handleNav('learn')                              — simple navigation
  //   handleNav('srt', { childId, prefillChildId })  — nav with state
  //   handleNav('learn', { linkedId, childId })       — parent flashcard launch
  const handleNav = (id, state) => {
    const path = id === 'dashboard' ? '/dashboard' : `/${id}`
    if (state) {
      navigate(path, { state })
    } else {
      navigate(path)
    }
  }

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--cream)', fontFamily:meta.family, direction:meta.dir, position:'relative' }}>

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position:'fixed', inset:0, zIndex:199, background:'rgba(0,0,0,.55)', backdropFilter:'blur(2px)' }}
        />
      )}

      <div style={{
        position:   isMobile ? 'fixed' : 'sticky',
        top:        0,
        m: 0, p: 0,
        left:       isMobile ? (sidebarOpen ? 0 : '-280px') : 0,
        zIndex:     isMobile ? 200 : 1,
        height:     '100vh',
        transition: isMobile ? 'left .28s cubic-bezier(.4,0,.2,1)' : 'none',
        flexShrink: 0,
      }}>
        <Sidebar
          active={active}
          onNav={handleNav}
          lang={lang}
          onLangChange={setLang}
          onMobileClose={isMobile ? () => setSidebarOpen(false) : undefined}
        />
      </div>

      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
        <TopBar
          lang={lang}
          onLangChange={setLang}
          onMenuToggle={isMobile ? () => setSidebarOpen(o => !o) : undefined}
          isMobile={isMobile}
        />
        <main style={{ flex:1, overflowY:'auto', overflowX:'hidden' }}>
          <Outlet context={{ handleNav, lang }}/>
        </main>
      </div>

      <SaveToast status={saveStatus}/>

      {/* <AccessibilityFloat /> */}
    </div>
  )
}

// ── Inner ─────────────────────────────────────────────────────────────────────
function Inner() {
  const { loaded, settings } = useStore()
  const { user }   = useAuth()
  const navigate   = useNavigate()
  const [lang, setLang] = useState('en')

  useEffect(() => {
    if (settings?.language) setLang(settings.language)
  }, [settings])

  const meta = LANG_META[lang] || LANG_META.en

  // Same two-arg handleNav used everywhere in Inner scope
  const handleNav = (id, state) => {
    const path = id === 'dashboard' ? '/dashboard' : `/${id}`
    if (state) {
      navigate(path, { state })
    } else {
      navigate(path)
    }
  }

  if (!loaded) return (
    <I18nProvider lang={lang}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16, background:'var(--cream)', fontFamily:meta.family }}>
        <div style={{ background:'var(--navy)', color:'white', fontWeight:900, fontSize:18, padding:'5px 13px', borderRadius:6 }}>NISH</div>
        <div style={{ width:24, height:24, border:'3px solid var(--sky)', borderTop:'3px solid transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>
        <div style={{ fontSize:13, color:'var(--mist)', fontWeight:600 }}>Loading platform data…</div>
      </div>
    </I18nProvider>
  )

  return (
    <I18nProvider lang={lang}>
      {/* <AccessibilityFloat style="margin:0"/> */}
      <Routes>

        {/* ── Public routes ── */}
        <Route path="/"              element={<LandingPage/>}/>
        <Route path="/landing"       element={<LandingPage/>}/>
        <Route path="/login"         element={user ? <Navigate to="/dashboard" replace/> : <LoginPage/>}/>
        <Route path="/auth/callback" element={<AuthCallback/>}/>
        <Route path="/unauthorized"  element={<UnauthorizedPage/>}/>
        <Route path="/quick-screener" element={<PublicQuickScreener />} />
        {/* ── Protected shell ── */}
        <Route element={<RequireAuth><Shell/></RequireAuth>}>

          <Route path="/dashboard" element={<RoleDashboard onNav={handleNav}/>}/>
          <Route path="/profile"   element={<ProfilePage/>}/>

          <Route path="/admin" element={
            <RequireRole roles={['superadmin', 'admin']}>
              <AdminPanel onNav={handleNav}/>
            </RequireRole>
          }/>

          <Route path="/research" element={
            <RequireRole roles={['superadmin', 'researcher']}>
              <ResearchDashboard onNav={handleNav}/>
            </RequireRole>
          }/>
          <Route path="/research/children" element={
            <RequireRole roles={['superadmin', 'researcher']}>
              <ResearchChildDashboard onNav={handleNav}/>
            </RequireRole>
          }/>
          <Route path="/research/variable/:variableId" element={
            <RequireRole roles={['superadmin', 'researcher']}>
              <ResearchVariablePage onNav={handleNav}/>
            </RequireRole>
          }/>
          <Route path="/research/calibration" element={
            <RequireRole roles={['superadmin', 'researcher']}>
              <ResearchCalibrationPage onNav={handleNav}/>
            </RequireRole>
          }/>
          <Route path="/clinical-ratings/new" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher']}>
              <ClinicalRatingForm onNav={handleNav}/>
            </RequireRole>
          }/>

          <Route path="/teacher" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher']}>
              <TeacherDashboard onNav={handleNav}/>
            </RequireRole>
          }/>

          <Route path="/collection"   element={<CollectionManager/>}/>
          <Route path="/progress"     element={<ProgressTracker/>}/>
          <Route path="/workplan"     element={<WorkPlan/>}/>
          <Route path="/batch-export" element={<BatchExport/>}/>

          <Route path="/learn"           element={<FlashcardLearning/>}/>
          <Route path="/worksheet-learn" element={<WorksheetLearning/>}/>
          <Route path="/activity-learn"  element={<ActivityCardLearning/>}/>
          <Route path="/storybook-learn" element={<StoryBookLearning/>}/>

          <Route path="/flashcard" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher', 'parent']}>
              <FlashcardGenerator/>
            </RequireRole>
          }/>
          <Route path="/worksheet" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher', 'parent']}>
              <WorksheetCreator/>
            </RequireRole>
          }/>
          <Route path="/activity" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher', 'parent']}>
              <ActivityCards/>
            </RequireRole>
          }/>
          <Route path="/storybook" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher', 'parent']}>
              <StoryBooks/>
            </RequireRole>
          }/>
          <Route path="/workplan" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher', 'parent']}>
              <WorkPlan/>
            </RequireRole>
          }/>
          <Route path="/progress" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher', 'parent']}>
              <ProgressTracker/>
            </RequireRole>
          }/>

          <Route path="/children" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher']}>
              <ChildrenView onNav={handleNav}/>
            </RequireRole>
          }/>
          <Route path="/readiness" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher']}>
              <SchoolReadiness/>
            </RequireRole>
          }/>

          {/* ── Quick Screener ── NEW */}
          <Route path="/srt-screener" element={
            <RequireRole roles={['superadmin', 'admin', 'teacher']}>
              <QuickScreener onNav={handleNav}/>
            </RequireRole>
          }/>

          <Route path="/settings" element={
            <RequireRole roles={['superadmin', 'admin']}>
              <SettingsView lang={lang} onLangChange={setLang}/>
            </RequireRole>
          }/>

          <Route path="/public-srt" element={
            <RequireRole roles={['superadmin']}>
              <PublicSRTReports/>
            </RequireRole>
          }/>

          <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
        </Route>

      </Routes>
    </I18nProvider>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <StoreProvider>
      <AuthProvider>
        <ErrorBoundary>
          <Inner/>
        </ErrorBoundary>
      </AuthProvider>
    </StoreProvider>
  )
}